import * as THREE from 'three';

/**
 * A procedural studio light probe, pre-filtered through PMREMGenerator and
 * assigned to scene.environment.
 *
 * This exists because a metal's appearance is its reflection: without something
 * to reflect, high metalness renders as flat dark plastic no matter how many
 * analytic lights are added. Building the probe in code rather than shipping an
 * .hdr means it cannot 404 and costs nothing to deploy.
 */

export interface SoftboxSpec {
  width: number;
  height: number;
  intensity: number;
  position: [number, number, number];
  tint: number;
}

/** One softbox per lighting job: key, cool fill, overhead streak, floor bounce, rear glint. */
export const STUDIO_SOFTBOXES: SoftboxSpec[] = [
  // The key is deliberately below 3: a mirror-metal rim at roughness 0.02
  // samples this panel directly, and anything brighter clips to flat white,
  // which reads as plastic rather than chrome.
  { width: 11, height: 7, intensity: 2.1, position: [-5.5, 6.0, 5.5], tint: 0xffffff },   // key
  { width: 9, height: 9, intensity: 0.85, position: [7.0, 1.5, 3.0], tint: 0xcddcea },    // cool fill
  { width: 4, height: 12, intensity: 1.35, position: [0.5, 7.6, -1.5], tint: 0xfff2dd },   // overhead streak
  { width: 14, height: 6, intensity: 0.45, position: [0, -5.5, 4.0], tint: 0xffe6c4 },     // warm floor bounce
  { width: 7, height: 5, intensity: 0.55, position: [1.5, 2.0, -8.0], tint: 0xdce8f5 },     // rear glint for bevels
];

/** Dark walls: a mid-grey room flattens the falloff and the metal goes milky. */
export const STUDIO_ROOM_COLOR = 0x2a3038;

export function buildStudioProbeScene(): THREE.Scene {
  const scene = new THREE.Scene();

  scene.add(
    new THREE.Mesh(
      new THREE.BoxGeometry(24, 16, 24),
      new THREE.MeshStandardMaterial({
        color: STUDIO_ROOM_COLOR,
        roughness: 1,
        metalness: 0,
        side: THREE.BackSide,
      })
    )
  );

  for (const box of STUDIO_SOFTBOXES) {
    const panel = new THREE.Mesh(
      new THREE.PlaneGeometry(box.width, box.height),
      // MeshBasicMaterial: these panels are light sources in the probe, not lit
      // surfaces, so their brightness must not depend on the room.
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(box.tint).multiplyScalar(box.intensity),
      })
    );
    panel.position.set(box.position[0], box.position[1], box.position[2]);
    panel.lookAt(0, 0, 0);
    scene.add(panel);
  }

  return scene;
}

/**
 * Returns null when there is no renderer or PMREM is unavailable, so the caller
 * keeps its analytic lights instead of throwing.
 */
export function buildStudioEnvironmentTexture(renderer: THREE.WebGLRenderer | null): THREE.Texture | null {
  if (!renderer || typeof THREE.PMREMGenerator !== 'function') return null;

  const probeScene = buildStudioProbeScene();
  try {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const target = pmrem.fromScene(probeScene, 0.04);
    pmrem.dispose();
    return target.texture;
  } catch (error) {
    console.warn('Studio environment probe unavailable, keeping analytic lights:', error);
    return null;
  } finally {
    probeScene.traverse((node) => {
      const mesh = node as THREE.Mesh;
      mesh.geometry?.dispose();
      if (Array.isArray(mesh.material)) mesh.material.forEach((material) => material.dispose());
      else mesh.material?.dispose();
    });
  }
}